import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { Header } from 'semantic-ui-react';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _startCase from 'lodash/startCase';

import HelpTooltip from 'components/common/HelpTooltip';
import EditFileField from 'components/modules/files/components/EditFileField';
import FilesManagementModalProvider from 'components/modules/files/components/FilesManagementModalProvider';
import { SOURCE_TYPE_DEFAULT } from 'components/modules/files/constants/sources';
import { getImageUrl } from 'components/modules/files/services/files';
import {
  getFileByAssociations,
  getFileProviderMetadata,
} from 'components/pages/BusinessProfilePage/components/MediaTab/MediaTab.utils';
import Utils from 'libs/utils';

import { HELP_TEXT_BY_ASSOCIATION } from './MediaSingleImageSelector.constants';
import { MEDIA_CATEGORY_PROFILE, MEDIA_CATEGORY_COVER } from '../../constants/media';
import { MediaContext } from '../../contexts/Media.context';
import MediaFileEditModal from '../MediaFileEditModal';

import './MediaSingleImageSelector.scss';

const propTypes = {
  association: PropTypes.oneOf([MEDIA_CATEGORY_PROFILE, MEDIA_CATEGORY_COVER]).isRequired,
};

const defaultProps = {};

export default function MediaSingleImageSelector({ association }) {
  const {
    selectedFiles: selectedGlobalFiles,
    filesMetadata,
    onSelectSingleImage,
    refreshFilesMetadata,
  } = useContext(MediaContext);

  const [fileEditModalOpen, setFileEditModalOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);

  const selectedFile = getFileByAssociations(selectedGlobalFiles, filesMetadata, [association]);
  const selectedFileId = _get(selectedFile, 'id', null);
  const isFileSelected = !_isEmpty(selectedFile);
  const selectedFileMetadata = getFileProviderMetadata(selectedFileId, filesMetadata);
  const selectedFileUrl = getImageUrl(selectedFile);

  async function onSelectFile(newSelectedFiles) {
    await onSelectSingleImage(association, _get(newSelectedFiles, '[0]', null));
  }

  function onFileDelete() {
    onSelectSingleImage(association, null);
  }

  function onFileUpload() {
    setFileUploadModalOpen(true);
  }

  function onFileEdit() {
    setFileUploadModalOpen(true);
  }

  const helpText = HELP_TEXT_BY_ASSOCIATION[association];
  const displayableAssociation = _startCase(association.toLowerCase());
  const isProfileAssociation = association === MEDIA_CATEGORY_PROFILE;
  const cardClassName = clsx('image-card', {
    'profile-aspect': isProfileAssociation,
    'cover-aspect': !isProfileAssociation,
  });

  return (
    <div className={clsx('media-single-image-selector', association.toLowerCase())}>
      <div className="header-container">
        <Header as="h3">{displayableAssociation}</Header>
        <HelpTooltip title={displayableAssociation} content={helpText} />
      </div>

      <div className="file-selection">
        <EditFileField
          className={cardClassName}
          url={isFileSelected ? Utils.addImageQueryParam(selectedFileUrl, 'width', 500) : ''}
          onUpload={onFileUpload}
          onChange={onFileEdit}
          onDelete={onFileDelete}
        />
      </div>

      {fileUploadModalOpen && (
        <FilesManagementModalProvider
          open={fileUploadModalOpen}
          setOpen={setFileUploadModalOpen}
          initialSelectedFiles={isFileSelected ? [selectedFile] : []}
          minFilesAllowed={null}
          maxFilesAllowed={1}
          onSubmit={onSelectFile}
          uploadSourceType={SOURCE_TYPE_DEFAULT}
        />
      )}

      {fileEditModalOpen && isFileSelected && (
        <MediaFileEditModal
          open={fileEditModalOpen}
          onClose={() => setFileEditModalOpen(false)}
          file={selectedFile}
          fileMetadata={selectedFileMetadata}
          onSave={refreshFilesMetadata}
        />
      )}
    </div>
  );
}

MediaSingleImageSelector.propTypes = propTypes;
MediaSingleImageSelector.defaultProps = defaultProps;
